import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Input,
} from '@zond/ui';
import { cn } from '@zond/utils';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cleanupEmail, extractEmails, isValidEmail, normalizeEmail } from '../utils/email-utils';

interface EmailItem {
  value: string;
  isValid: boolean;
}

interface MultiEmailInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: EmailItem[];
  onChange: (emails: EmailItem[]) => void;
  onValidityChange?: (isValid: boolean) => void;
  className?: string;
}

export const MultiEmailInput = React.forwardRef<HTMLInputElement, MultiEmailInputProps>(
  ({ value, onChange, onValidityChange, className, ...props }, ref) => {
    const { t } = useTranslation('settings');
    const [inputValue, setInputValue] = useState('');
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const editInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      // Check validity of all email addresses
      const isValid = value.every((item) => item.isValid);
      onValidityChange?.(isValid);
    }, [value, onValidityChange]);

    const isEmailDuplicate = (email: string, skipIndex?: number) => {
      const normalizedNew = normalizeEmail(email);
      return value.some((item, index) => index !== skipIndex && normalizeEmail(item.value) === normalizedNew);
    };

    const addEmail = (email: string) => {
      const cleanEmail = cleanupEmail(email);
      if (cleanEmail && !isEmailDuplicate(cleanEmail)) {
        const newEmailItem: EmailItem = {
          value: cleanEmail,
          isValid: isValidEmail(cleanEmail),
        };
        onChange([...value, newEmailItem]);
        return true;
      }
      return false;
    };

    const updateEmail = (index: number, newValue: string) => {
      const cleanEmail = cleanupEmail(newValue);
      if (cleanEmail && !isEmailDuplicate(cleanEmail, index)) {
        const newEmails = [...value];
        newEmails[index] = {
          value: cleanEmail,
          isValid: isValidEmail(cleanEmail),
        };
        onChange(newEmails);
        return true;
      }
      return false;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      // If a delimiter is entered, try to add the email
      if (/[,;\s]$/.test(newValue)) {
        const email = newValue.slice(0, -1);
        if (addEmail(email)) {
          setInputValue('');
          return;
        }
      }

      setInputValue(newValue);
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const newEmails = extractEmails(pastedText, false);

      if (newEmails.length > 0) {
        // Filter unique emails
        const uniqueEmails = newEmails.filter((email) => !isEmailDuplicate(email));

        if (uniqueEmails.length > 0) {
          const newEmailItems = uniqueEmails.map((email) => ({
            value: email,
            isValid: isValidEmail(email),
          }));
          onChange([...value, ...newEmailItems]);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (editingIndex !== null) {
          if (updateEmail(editingIndex, editValue)) {
            setEditingIndex(null);
            setEditValue('');
          }
        } else if (addEmail(inputValue)) {
          setInputValue('');
        }
      } else if (e.key === 'Escape') {
        if (editingIndex !== null) {
          setEditingIndex(null);
          setEditValue('');
        }
      } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0 && editingIndex === null) {
        // Delete the last email when pressing Backspace
        onChange(value.slice(0, -1));
      }
    };

    const handleBlur = () => {
      // When focus is lost, try to add current input as email
      if (inputValue.trim()) {
        if (addEmail(inputValue)) {
          setInputValue('');
        }
      }
    };

    const removeEmail = (emailToRemove: string) => {
      onChange(value.filter((item) => item.value !== emailToRemove));
    };

    const startEditing = (index: number) => {
      setEditingIndex(index);
      setEditValue(value[index].value);
    };

    const handleEditConfirm = (index: number) => {
      if (updateEmail(index, editValue)) {
        setEditingIndex(null);
        setEditValue('');
      }
    };

    const handleEditCancel = () => {
      setEditingIndex(null);
      setEditValue('');
    };

    const handleClearAll = () => {
      onChange([]);
      setInputValue('');
    };

    return (
      <div className="relative">
        <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[38px] focus-within:outline-none focus-within:ring-1 focus-within:ring-ring">
          {value.map((item, index) =>
            editingIndex === index ? (
              <div
                key={`editing-${item.value}`}
                className="flex items-center bg-background rounded-md border h-7 w-fit"
              >
                <div className="relative flex-1 min-w-0">
                  <Input
                    ref={editInputRef}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEditConfirm(index);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        handleEditCancel();
                      }
                    }}
                    autoFocus
                    className="!h-7 !py-0 !pl-2 !pr-1 w-[1px] min-w-0 border-0 focus-visible:ring-0"
                    style={{ width: `${Math.max(editValue.length, item.value.length) + 2}ch` }}
                  />
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEditConfirm(index)}
                    className={cn(
                      'group-hover:opacity-100 transition-opacity p-0.5',
                      item.isValid
                        ? 'text-muted-foreground hover:text-foreground opacity-70'
                        : 'text-destructive/70 hover:text-destructive opacity-100'
                    )}
                  >
                    <Check className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={handleEditCancel}
                    className={cn(
                      'group-hover:opacity-100 transition-opacity p-0.5',
                      item.isValid
                        ? 'text-muted-foreground hover:text-foreground opacity-70'
                        : 'text-destructive/70 hover:text-destructive opacity-100'
                    )}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={item.value}
                className={cn(
                  'flex items-center gap-1 px-2 py-1 text-sm rounded group',
                  item.isValid ? 'bg-primary/10' : 'bg-destructive/10 text-destructive'
                )}
                title={!item.isValid ? t('members.emailInput.invalidTitle') : undefined}
              >
                <span>{item.value}</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startEditing(index)}
                    className={cn(
                      'group-hover:opacity-100 transition-opacity p-0.5',
                      item.isValid
                        ? 'text-muted-foreground hover:text-foreground opacity-70'
                        : 'text-destructive/70 hover:text-destructive opacity-100'
                    )}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeEmail(item.value)}
                    className={cn(
                      'group-hover:opacity-100 transition-opacity',
                      item.isValid
                        ? 'text-muted-foreground hover:text-foreground opacity-70'
                        : 'text-destructive/70 hover:text-destructive opacity-100'
                    )}
                  >
                    ×
                  </button>
                </div>
              </div>
            )
          )}
          {editingIndex === null && (
            <Input
              {...props}
              ref={ref}
              value={inputValue}
              onChange={handleInputChange}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={value.length === 0 ? props.placeholder : undefined}
              className={cn(
                'shadow-none border-0 focus-visible:ring-0 p-0 h-6 flex-1 min-w-[120px]',
                className
              )}
            />
          )}
        </div>
      </div>
    );
  }
);

MultiEmailInput.displayName = 'MultiEmailInput';
